import React from 'react';
import AddIcon from '@atlaskit/icon/core/migration/add';
import { Section } from '@atlaskit/side-navigation';
import Tooltip from '@atlaskit/tooltip';
import { CORE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { TitleHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { IssueTypesGroup } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/issue-types-group/main.tsx';
import { Skeleton } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/skeleton/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { useAddIssueTypeContext } from '@atlassian/jira-project-settings-apps-add-issue-type/src/controllers/context/index.tsx';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import { useIssueTypesNavigationItems as useIssueTypesNavigationItemsNext } from '@atlassian/jira-spa-apps-project-settings-issue-type/src/controllers/issue-types-navigation-items/index.tsx';
import messages from './messages.tsx';

export const useThrowProject = withThrowOnError(useProject);

export const IssueTypes = () => {
	const { formatMessage } = useIntl();

	const [
		{ asyncState, topLevel, baseLevel, bottomLevel, addIssueTypeTooltip, isAddIssueTypeDisabled },
	] = useIssueTypesNavigationItemsNext();
	const [, addIssueTypesContextActions] = useAddIssueTypeContext();
	const { data: project } = useThrowProject();
	let baseSettingsUrl = '';
	if (project !== null) {
		baseSettingsUrl = getNextgenSettingsUrl(CORE_PROJECT, project.key);
	}

	const hasAnyIssueTypes = topLevel.length > 0 || baseLevel.length > 0 || bottomLevel.length > 0;
	const isReady = asyncState === 'SUCCESS' || hasAnyIssueTypes;

	if (!project || !isReady) return <Skeleton />;

	return (
		<Section>
			<TitleHeader>
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.issueTypesIssueTermRefresh
						: messages.issueTypes,
				)}
			</TitleHeader>
			<IssueTypesGroup
				baseSettingsUrl={baseSettingsUrl}
				hasSeparator={false}
				navigationItems={topLevel}
			/>
			<IssueTypesGroup baseSettingsUrl={baseSettingsUrl} hasSeparator navigationItems={baseLevel} />
			<IssueTypesGroup
				baseSettingsUrl={baseSettingsUrl}
				hasSeparator
				navigationItems={bottomLevel}
			/>
			<Section>
				{(() => {
					const addItem = (
						<MenuItem
							analytics={{ itemId: 'add-issue-type' }}
							iconBefore={
								<AddIcon
									spacing="spacious"
									label={formatMessage(
										fg('jira-issue-terminology-refresh-m3')
											? messages.addIssueTypeIssueTermRefresh
											: messages.addIssueType,
									)}
								/>
							}
							isDisabled={isAddIssueTypeDisabled}
							onClick={
								isAddIssueTypeDisabled
									? undefined
									: addIssueTypesContextActions.openAddIssueTypeModal
							}
						>
							{formatMessage(
								fg('jira-issue-terminology-refresh-m3')
									? messages.addIssueTypeIssueTermRefresh
									: messages.addIssueType,
							)}
						</MenuItem>
					);

					return addIssueTypeTooltip ? (
						<Tooltip content={addIssueTypeTooltip}>{addItem}</Tooltip>
					) : (
						addItem
					);
				})()}
			</Section>
		</Section>
	);
};
